/**
 * Catalogue of pages a user may pin as a home-page favourite.
 *
 * Gating reuses the same permission helpers as Sidebar.tsx and the ProtectedRoute
 * guards in App.tsx, so a page is only offered if the user can actually open it.
 * Without that, a favourite pinned under one role could outlive a role change and
 * leave a shortcut that lands on the access-denied screen.
 *
 * A couple of entries (New Expense, Settings) are reachable from the shell rather
 * than the sidebar tree, but are gated against their route guard just the same.
 */

import { User, UserRole } from '../types/user';
import { NominationRole } from '../types/nomination';
import {
  canViewTeamExpenses,
  canViewAllExpenses,
  canManageUsers,
  canAccessShiftRoster,
  canManageShiftMaster,
  canAccessShiftReports,
} from './rolePermissions';
import { getNominationMenuItems } from './nominationPermissions';
import { mapUserRoleToPMSRole, getPMSMenuItems, hasModuleAccess } from './pmsPermissions';

export interface FavoritePage {
  /** Route path — also the stable identity of the favourite in storage. */
  path: string;
  label: string;
  /** Section heading used to group the picker. */
  group: string;
}

/** Cap on pinned favourites, so the home row stays a shortcut strip and not a menu. */
export const MAX_FAVORITES = 8;

/**
 * Every page the given user is allowed to open, in sidebar order.
 * `nominationRole` comes from useNominationRole; pass null before it resolves.
 */
export const getFavoritablePages = (
  user: User | null | undefined,
  nominationRole: NominationRole | null | undefined
): FavoritePage[] => {
  if (!user?.role) return [];

  const role = user.role;
  const pages: FavoritePage[] = [];

  // ── Expenses ──
  pages.push({ path: '/expenses/my', label: 'My Expenses', group: 'Expenses' });
  pages.push({ path: '/expenses/new', label: 'New Expense', group: 'Expenses' });
  if (canViewTeamExpenses(role)) {
    pages.push({ path: '/manager', label: 'Team Approvals', group: 'Expenses' });
  }
  if (canViewAllExpenses(role)) {
    pages.push({ path: '/admin', label: 'Finance', group: 'Expenses' });
  }

  // ── Leave ──
  const isLeaveManager = role === UserRole.MANAGER || role === UserRole.HR_ADMIN || role === UserRole.SUPER_ADMIN;
  const isLeaveHR = role === UserRole.HR_ADMIN || role === UserRole.SUPER_ADMIN;
  pages.push({ path: '/leave/dashboard', label: 'Leave Dashboard', group: 'Leave' });
  pages.push({ path: '/leave/apply', label: 'Apply Leave', group: 'Leave' });
  if (isLeaveManager) {
    pages.push({ path: '/leave/team-matrix', label: 'Team Leave Matrix', group: 'Leave' });
  }
  if (isLeaveHR) {
    pages.push({ path: '/leave/policies', label: 'Leave Policies', group: 'Leave' });
    pages.push({ path: '/leave/hr-operations', label: 'HR Operations', group: 'Leave' });
    pages.push({ path: '/leave/reports', label: 'Leave Reports', group: 'Leave' });
  }

  // ── Timesheets ──
  pages.push({ path: '/timesheet', label: 'My Timesheet', group: 'Timesheets' });
  if (canViewTeamExpenses(role)) {
    pages.push({ path: '/timesheet/team', label: 'Team Timesheets', group: 'Timesheets' });
  }
  if (role === UserRole.SUPER_ADMIN || role === UserRole.HR_ADMIN) {
    pages.push({ path: '/timesheet/projects', label: 'Projects & Tasks', group: 'Timesheets' });
  }

  // ── Shift Roster ──
  // One gate per route, as this file's contract requires. Shift Details is now a read-only view
  // for everyone; Shift Configuration is HR/Super only (its route guard always was, so offering
  // it to a Manager here produced a favourite that redirected on click); Bulk Override writes,
  // so it follows the edit permission.
  pages.push({ path: '/shift-roster/shift-details', label: 'Shift Details', group: 'Shift Roster' });
  if (canManageShiftMaster(role)) {
    pages.push({ path: '/shift-roster/shift-configuration', label: 'Shift Configuration', group: 'Shift Roster' });
  }
  if (canAccessShiftRoster(role)) {
    pages.push({ path: '/shift-roster/bulk-override', label: 'Bulk Override', group: 'Shift Roster' });
  }
  // Reports is HR/Super only — a Manager favouriting it would get a redirect on click.
  if (canAccessShiftReports(role)) {
    pages.push({ path: '/shift-roster/reports', label: 'Shift Reports', group: 'Shift Roster' });
  }

  // ── Attendance ──
  // My Attendance is everyone's own; the admin screens follow canManageUsers, the same gate the
  // panes and the sidebar use. One gate per route, as this file's contract requires.
  pages.push({ path: '/attendance/my-attendance', label: 'My Attendance', group: 'Attendance' });
  /*
   * Manual Attendance is deliberately NOT offered as a favourite.
   *
   * Whether an employee may record their own attendance is a per-person configuration an admin
   * can withdraw, and this file's contract is one permission gate per route — evaluated from the
   * role, synchronously, with no I/O. A favourite that survived the permission being withdrawn
   * would redirect on click, which is exactly the defect the shift-roster entries above were
   * fixed for. The sidebar offers the link while it applies, which is the right place for a
   * signal that can change.
   */
  // Everyone can raise a regularization request for one of their own days.
  pages.push({ path: '/attendance/regularization', label: 'Regularization', group: 'Attendance' });
  if (canManageUsers(role)) {
    pages.push({ path: '/attendance/admin', label: 'Admin Attendance', group: 'Attendance' });
    pages.push({ path: '/attendance/raw-punches', label: 'Raw Punches', group: 'Attendance' });
    pages.push({ path: '/attendance/device-sync', label: 'Device Sync', group: 'Attendance' });
    pages.push({ path: '/attendance/reports', label: 'Attendance Reports', group: 'Attendance' });
  }

  // ── Award Nominations ──
  // HR_Admin is restricted from this module even though the nomination role resolves.
  if (hasModuleAccess(role, 'AWARD_NOMINATION') && nominationRole) {
    getNominationMenuItems(nominationRole).forEach(item => {
      pages.push({ path: item.path, label: item.label, group: 'Award Nominations' });
    });
  }

  // ── Performance ──
  getPMSMenuItems(mapUserRoleToPMSRole(role)).forEach(item => {
    pages.push({ path: item.path, label: item.label, group: 'Performance' });
  });

  // ── Administration ──
  // Both routes are guarded by requiredPermission="admin", i.e. canManageUsers.
  if (canManageUsers(role)) {
    pages.push({ path: '/super-admin', label: 'User Management', group: 'Administration' });
    pages.push({ path: '/settings', label: 'Settings', group: 'Administration' });
  }

  return pages;
};

/** Preserves catalogue order rather than the order paths were pinned in. */
export const resolveFavorites = (paths: string[], catalog: FavoritePage[]): FavoritePage[] => {
  const pinned = new Set(paths);
  return catalog.filter(page => pinned.has(page.path));
};

export const groupPages = (pages: FavoritePage[]): { group: string; pages: FavoritePage[] }[] => {
  const groups: { group: string; pages: FavoritePage[] }[] = [];
  pages.forEach(page => {
    const existing = groups.find(g => g.group === page.group);
    if (existing) existing.pages.push(page);
    else groups.push({ group: page.group, pages: [page] });
  });
  return groups;
};
